// frontend/src/services/notificationService.js - VERSION AVEC DÉDUPLICATION
import api from './api';

class NotificationService {
  constructor() {
    this.socket = null;
    this.cache = {
      notifications: [],
      lastFetch: null,
      isValid: false
    };
    this.listeners = new Set();
    this.isConnecting = false;
    this.retryAttempts = 0;
    this.maxRetries = 3;
    this.retryDelay = 1000;
  }

  // ========== CACHE MANAGEMENT ==========
  
  setCacheData(notifications) {
    console.log('📦 Mise à jour cache avec', notifications.length, 'notifications');
    
    // 🔥 NOUVEAU : Déduplication avant mise en cache
    const deduplicatedNotifications = this.deduplicateNotifications(notifications);
    console.log('🔄 Après déduplication:', deduplicatedNotifications.length, 'notifications');
    
    this.cache = {
      notifications: deduplicatedNotifications,
      lastFetch: Date.now(),
      isValid: true
    };
  }

  // 🔥 NOUVELLE MÉTHODE : Déduplication des notifications
  deduplicateNotifications(newNotifications) {
    const existingIds = new Set(this.cache.notifications.map(n => n._id));
    const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n._id));
    
    // Fusionner et trier par date (plus récent en premier)
    const allNotifications = [...uniqueNewNotifications, ...this.cache.notifications];
    
    // Supprimer les doublons basés sur l'ID
    const deduplicatedMap = new Map();
    allNotifications.forEach(notification => {
      if (!deduplicatedMap.has(notification._id)) {
        deduplicatedMap.set(notification._id, notification);
      }
    });
    
    return Array.from(deduplicatedMap.values())
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  isCacheValid() {
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    return this.cache.isValid && 
           this.cache.lastFetch && 
           (Date.now() - this.cache.lastFetch) < CACHE_DURATION;
  }

  invalidateCache() {
    console.log('🗑️ Cache invalidé');
    this.cache.isValid = false;
  }

  // ========== WEBSOCKET MANAGEMENT ==========

  initWebSocket(token) {
    if (this.socket?.connected || this.isConnecting) {
      console.log('⚠️ WebSocket déjà connecté ou en cours de connexion');
      return;
    }

    console.log('🔌 Initialisation WebSocket...');
    this.isConnecting = true;
    
    try {
      import('socket.io-client').then(({ default: io }) => {
        const serverUrl = process.env.NODE_ENV === 'production' 
          ? 'https://etatonlinev01.onrender.com'
          : 'http://localhost:5000';

        console.log('🌐 Connexion WebSocket vers:', serverUrl);

        this.socket = io(serverUrl, {
          auth: { token },
          transports: ['websocket', 'polling'],
          upgrade: true,
          rememberUpgrade: true,
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 2000,
          forceNew: true
        });

        this.setupSocketListeners();
        this.isConnecting = false;

      }).catch(error => {
        console.error('❌ Erreur import socket.io-client:', error);
        this.isConnecting = false;
      });

    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation WebSocket:', error);
      this.isConnecting = false;
    }
  }

  setupSocketListeners() {
    if (!this.socket) {
      console.error('❌ Impossible de configurer les listeners - socket null');
      return;
    }

    console.log('🔧 Configuration des listeners WebSocket...');

    this.socket.on('connect', () => {
      console.log('✅ WebSocket connecté! ID:', this.socket.id);
      this.retryAttempts = 0;
      this.notifyListeners('connected', true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket déconnecté:', reason);
      this.notifyListeners('connected', false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Erreur de connexion WebSocket:', error);
      this.notifyListeners('connected', false);
    });

    this.socket.on('connected', (data) => {
      console.log('🎉 Confirmation serveur:', data);
    });

    // 🔥 GESTION AMÉLIORÉE : Nouvelle notification en temps réel
    this.socket.on('new_notification', (notification) => {
      console.log('🔔 NOUVELLE NOTIFICATION REÇUE via WebSocket:', notification);
      
      // Vérifier si la notification existe déjà
      const exists = this.cache.notifications.find(n => n._id === notification._id);
      if (exists) {
        console.log('⚠️ Notification déjà présente, ignorée:', notification._id);
        return;
      }
      
      // Ajouter au début du cache (plus récent en premier)
      const updatedNotifications = [notification, ...this.cache.notifications];
      this.setCacheData(updatedNotifications);
      
      // Notifier les composants
      this.notifyListeners('new_notification', notification);
    });

    this.socket.on('notification_read', (notificationId) => {
      console.log('📖 Notification marquée comme lue:', notificationId);
      
      this.cache.notifications = this.cache.notifications.map(notif => 
        notif._id === notificationId ? { ...notif, read: true } : notif
      );
      this.setCacheData(this.cache.notifications);
      
      this.notifyListeners('notification_updated', notificationId);
    });

    this.socket.on('all_notifications_read', () => {
      console.log('📖 Toutes les notifications marquées comme lues');
      
      this.cache.notifications = this.cache.notifications.map(notif => 
        ({ ...notif, read: true })
      );
      this.setCacheData(this.cache.notifications);
      
      this.notifyListeners('all_notifications_read', true);
    });

    this.socket.on('notification_deleted', (notificationId) => {
      console.log('🗑️ Notification supprimée:', notificationId);
      
      this.cache.notifications = this.cache.notifications.filter(
        notif => notif._id !== notificationId
      );
      this.setCacheData(this.cache.notifications);
      
      this.notifyListeners('notification_deleted', notificationId);
    });

    this.socket.on('pong', () => {
      console.log('🏓 Pong reçu du serveur');
    });

    this.socket.on('error', (error) => {
      console.error('❌ Erreur WebSocket générale:', error);
    });

    console.log('✅ Listeners WebSocket configurés');
  }

  disconnectWebSocket() {
    if (this.socket) {
      console.log('🔌 Déconnexion WebSocket...');
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  // ========== API CALLS ==========

  async apiCallWithRetry(apiCall, retryCount = 0) {
    try {
      return await apiCall();
    } catch (error) {
      if (error.response?.status === 429 && retryCount < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, retryCount);
        console.log(`⏳ Rate limit, retry dans ${delay}ms (${retryCount + 1}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.apiCallWithRetry(apiCall, retryCount + 1);
      }
      
      throw error;
    }
  }

  // ========== PUBLIC API ==========

  async getNotifications(forceRefresh = false) {
    try {
      console.log('📥 getNotifications appelé, forceRefresh:', forceRefresh);

      // Si on a des notifications en cache et pas de forceRefresh, utiliser le cache
      if (!forceRefresh && this.isCacheValid()) {
        console.log('📦 Retour depuis cache valide');
        return {
          success: true,
          data: this.cache.notifications,
          source: 'cache'
        };
      }

      console.log('🌐 Appel API pour les notifications...');
      const apiCall = () => api.get('/notifications');
      const response = await this.apiCallWithRetry(apiCall);
      
      console.log('📊 Réponse API notifications:', {
        count: response.data.data.length,
        firstFew: response.data.data.slice(0, 3).map(n => ({ id: n._id, message: n.message.substring(0, 50) }))
      });

      // 🔥 AMÉLIORATION : Fusion intelligente avec le cache existant
      if (this.cache.notifications.length > 0) {
        console.log('🔄 Fusion avec cache existant...');
        
        // Marquer les nouvelles notifications depuis l'API
        const apiNotifications = response.data.data.map(n => ({ ...n, source: 'api' }));
        
        // Fusionner avec déduplication
        this.setCacheData(apiNotifications);
      } else {
        // Premier chargement, pas de cache
        this.setCacheData(response.data.data);
      }
      
      return {
        success: true,
        data: this.cache.notifications,
        source: 'api'
      };

    } catch (error) {
      console.error('❌ Erreur getNotifications:', error);
      
      if (this.cache.notifications.length > 0) {
        console.log('📦 Fallback vers cache expiré');
        return {
          success: true,
          data: this.cache.notifications,
          source: 'cache_fallback',
          warning: 'Données possiblement obsolètes'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la récupération des notifications',
        data: []
      };
    }
  }

  async markAsRead(notificationId) {
    try {
      console.log('📖 Marquage notification comme lue:', notificationId);
      
      const apiCall = () => api.put(`/notifications/${notificationId}/read`);
      const response = await this.apiCallWithRetry(apiCall);

      // Mise à jour optimiste du cache
      this.cache.notifications = this.cache.notifications.map(notif => 
        notif._id === notificationId ? { ...notif, read: true } : notif
      );
      this.setCacheData(this.cache.notifications);

      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Erreur markAsRead:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors du marquage de la notification'
      };
    }
  }

  async markAllAsRead() {
    try {
      console.log('📖 Marquage toutes notifications comme lues');
      
      const apiCall = () => api.put('/notifications/read-all');
      const response = await this.apiCallWithRetry(apiCall);

      this.cache.notifications = this.cache.notifications.map(notif => 
        ({ ...notif, read: true })
      );
      this.setCacheData(this.cache.notifications);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Erreur markAllAsRead:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors du marquage des notifications'
      };
    }
  }

  async deleteNotification(notificationId) {
    try {
      console.log('🗑️ Suppression notification:', notificationId);
      
      const apiCall = () => api.delete(`/notifications/${notificationId}`);
      const response = await this.apiCallWithRetry(apiCall);

      this.cache.notifications = this.cache.notifications.filter(
        notif => notif._id !== notificationId
      );
      this.setCacheData(this.cache.notifications);

      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Erreur deleteNotification:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la suppression de la notification'
      };
    }
  }

  // ========== LISTENER MANAGEMENT ==========

  addListener(callback) {
    console.log('🎧 Ajout d\'un listener');
    this.listeners.add(callback);
    
    return () => {
      console.log('🎧 Suppression d\'un listener');
      this.listeners.delete(callback);
    };
  }

  notifyListeners(event, data) {
    console.log(`📢 Notification aux listeners: ${event}`, data);
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('❌ Erreur dans listener notification:', error);
      }
    });
  }

  // ========== UTILITIES ==========

  getUnreadCount() {
    const count = this.cache.notifications.filter(notif => !notif.read).length;
    console.log('🔢 Nombre notifications non lues:', count);
    return count;
  }

  isConnected() {
    const connected = this.socket?.connected || false;
    console.log('🔌 État connexion WebSocket:', connected);
    return connected;
  }

  testConnection() {
    if (this.socket?.connected) {
      console.log('🧪 Test de connexion WebSocket...');
      this.socket.emit('ping');
      return true;
    } else {
      console.log('❌ WebSocket non connecté pour le test');
      return false;
    }
  }

  cleanup() {
    console.log('🧹 Nettoyage du service de notifications');
    this.disconnectWebSocket();
    this.listeners.clear();
    this.invalidateCache();
  }
}

// Instance singleton
const notificationService = new NotificationService();

export default notificationService;